import { createSlice } from "@reduxjs/toolkit";
import {
  attributeFiltersSelectedInitialState,
  filterOptionsInitialState,
  filterOptionsSelectedInitialState,
  initialParameters,
  sortByOptionsInitialState,
} from "../../utils/constants";

const initialState = {
  isResetPageOnOpen: true,
  collectionDetails: null,
  productsLoading: true,
  moreProductsLoading: true,
  page: 1,
  maxProductPages: 1,
  collectionLoading: true,
  searchText: "",
  collectionTriggers: null,
  parameters: initialParameters,
  sortByOptions: sortByOptionsInitialState,
  sortByOptionSelected: "",
  filterOptionsLoading: true,
  filterOptions: filterOptionsInitialState,
  filterOptionsSelected: filterOptionsSelectedInitialState,
  attributeFiltersSelected: attributeFiltersSelectedInitialState,
  attributeFiltersOptions: [],
  attributeFiltersLoading: false,
  /** SEL-1412: CLP quick-filter tile -> open the filter drawer on this section
   * (`"attr:<name>"`); consumed once by the drawer, then cleared. */
  filterDrawerInitialSection: null,
  dropOffSurvey: { show: false, brandId: null, collectionId: null },
};

export const collectionsSlice = createSlice({
  name: "sourcingCollections",
  initialState: initialState,
  reducers: {
    setResetPageOnCollectionOpen(state, action) {
      state.isResetPageOnOpen = action.payload;
    },
    updateSourcingCollectionDetails(state, action) {
      state.collectionDetails = action.payload;
    },
    updateSourcingCollectionHeader(state, action) {
      state.collectionHeader = action.payload;
    },
    updateSourcingCollectionProducts(state, action) {
      state.maxPrice = action.payload?.max_price;
      state.minPrice = action.payload?.min_price;
      state.maxProductPages = action.payload?.num_pages;
    },
    increaseSourcingCollectionProductPage(state, action) {
      state.page = state.page + 1;
    },
    updateSourcingCollectionLoading(state, action) {
      state.collectionLoading = action.payload;
    },
    updateSourcingCollectionProductsLoading(state, action) {
      state.productsLoading = action.payload;
    },
    updateSourcingCollectionMoreProductsLoading(state, action) {
      state.moreProductsLoading = action.payload;
    },
    setSearchText(state, action) {
      state.searchText = action.payload;
      state.page = 1;
    },
    updateCollectionTriggers(state, action) {
      state.collectionTriggers = action.payload;
    },
    setSortByOptionSelected(state, action) {
      state.page = 1;
      state.sortByOptionSelected = action.payload;
    },
    /** Only reset listing page — keep collectionDetails/filters when e.g. plpCollectionId toggles */
    resetListingPageOnly(state) {
      state.page = 1;
    },
    /** Sync listing page to already-loaded cached pages (back from PDP) so infinite scroll continues from where the user left off */
    setListingPage(state, action) {
      state.page = action.payload;
    },
    /** Clear filters/search when entering curated PLP collection — collections are filter-independent */
    resetAppliedListingFilters(state) {
      state.filterOptionsSelected = filterOptionsSelectedInitialState;
      state.attributeFiltersSelected = attributeFiltersSelectedInitialState;
      state.searchText = "";
      state.page = 1;
      // SEL-1101: sort is scoped per listing context — clear it on context switch so a
      // sort applied on the main PLP doesn't carry into a curated collection (or vice versa).
      state.sortByOptionSelected = "";
    },
    resetCollectionPage(state, action) {
      state.productsLoading = true;
      state.isResetPageOnOpen = true;
      state.collectionDetails = null;
      state.products = [];
      state.productPage = 1;
      state.maxProductPages = 1;
      state.searchText = "";
      state.page = 1;

      state.parameters = initialParameters;
      state.filterOptions = filterOptionsInitialState;
      state.filterOptionsSelected = filterOptionsSelectedInitialState;
      state.attributeFiltersSelected = attributeFiltersSelectedInitialState;
      state.attributeFiltersOptions = [];
      state.sortByOption = sortByOptionsInitialState;
      state.sortByOptionSelected = "";
    },
    resetCollectionProducts(state, action) {
      state.products = [];
      state.productPage = 1;
      state.maxProductPages = 1;
    },
    setParameters(state, action) {
      state.parameters = action.payload;
    },

    // FILTER OPTIONS
    setFilterOptionsLoading(state, action) {
      state.filterOptionsLoading = action.payload;
    },
    updateSourcingFilterSizeOptions(state, action) {
      state.filterOptions[0].options = action.payload;
    },
    updateSourcingFilterBrandOptions(state, action) {
      state.filterOptions[2].options = action.payload;
    },
    updateSourcingFilterCategoriesOptions(state, action) {
      state.filterOptions[3].options = action.payload;
    },
    updateSourcingFilterDiscountOptions(state, action) {
      state.filterOptions[4].options = action.payload;
    },
    updateSourcingFilterTagsOptions(state, action) {
      state.filterOptions[6].options = action.payload;
    },
    updateSourcingFilterGenderOptions(state, action) {
      state.filterOptions[7].options = action.payload;
    },
    updateSourcingFilterTrendsOptions(state, action) {
      state.filterOptions[8].options = action.payload;
    },
    setFilterOptionsSelected(state, action) {
      state.filterOptionsSelected = action.payload;
      state.page = 1;
    },
    setPriceRangeFilter(state, action) {
      state.filterOptionsSelected[4] = {
        value: "price",
        options: action.payload,
      };
      state.page = 1;
    },
    setAttributeFiltersSelected(state, action) {
      state.attributeFiltersSelected = action.payload;
      state.page = 1;
    },
    updateAttributeFiltersOptions(state, action) {
      state.attributeFiltersOptions = action.payload;
    },
    setAttributeFiltersLoading(state, action) {
      state.attributeFiltersLoading = action.payload;
    },
    setFilterDrawerInitialSection(state, action) {
      state.filterDrawerInitialSection = action.payload;
    },
    showDropOffSurvey(state, action) {
      state.dropOffSurvey = {
        show: true,
        brandId: action.payload.brandId,
        collectionId: action.payload.collectionId,
      };
    },
    hideDropOffSurvey(state) {
      state.dropOffSurvey = { show: false, brandId: null, collectionId: null };
    },
  },
});

export const {
  setResetPageOnCollectionOpen,
  resetListingPageOnly,
  setListingPage,
  resetAppliedListingFilters,
  updateSourcingCollectionDetails,
  updateSourcingCollectionProducts,
  increaseSourcingCollectionProductPage,
  updateSourcingCollectionLoading,
  updateSourcingCollectionProductsLoading,
  updateSourcingCollectionMoreProductsLoading,
  setSearchText,
  updateCollectionTriggers,
  setSortByOptionSelected,
  resetCollectionPage,
  resetCollectionProducts,
  setParameters,
  setPriceRangeFilter,
  setFilterOptionsLoading,
  updateSourcingFilterSizeOptions,
  updateSourcingFilterBrandOptions,
  updateSourcingFilterCategoriesOptions,
  updateSourcingFilterDiscountOptions,
  updateSourcingFilterTagsOptions,
  updateSourcingFilterGenderOptions,
  updateSourcingFilterTrendsOptions,
  setFilterOptionsSelected,
  setAttributeFiltersSelected,
  updateAttributeFiltersOptions,
  setAttributeFiltersLoading,
  setFilterDrawerInitialSection,
  showDropOffSurvey,
  hideDropOffSurvey,
} = collectionsSlice.actions;
